<script setup lang="ts">
import useSwr from "@/core/composables/swr/useSwr";
import {cacheKey} from "@/lib/cache";
import {massCertificationApi} from "@/container";
import ConsumerAnimationCell from "@/core/components/animation/ConsumerAnimationCell.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import {MassCertificationCustomerRequest} from "@/core/http/resources/MassCertificationResource";
import ConsumerAnimationTopSales from "@/core/components/animation/ConsumerAnimationTopSales.vue";
import {onMounted, ref} from "vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {pluralize} from "@/core/filters/Pluralize";
import {useCommunicationStore} from "@/communication/stores/communication.store";
import MassCertificationService from "@/communication/services/MassCertificationService";
import {useRoute, useRouter} from "vue-router";
import dateHelper from "../../core/helpers/dateHelper";
import dayjs from "dayjs";

const router = useRouter();
const route = useRoute();
const communicationStore = useCommunicationStore();

onMounted(() => {
    communicationStore.$reset();
});

const params: MassCertificationCustomerRequest = {
    query: {
        "minimum_visits": [1],
        "last_visit_range": [{
            from: dayjs().format('YYYY-MM-DD'),
            to: null
        }]
    },
    top_sales: 1
};

const {data, isValidating} = useSwr(
    cacheKey('mass-certification/customers', params),
    () => massCertificationApi().customers(params)
);

const selected = ref<number[]>([]);

const handleSend = () => {
    const selectedCustomers = data.value?.data.filter(customer => selected.value.includes(customer.id)) ?? [];
    communicationStore.customers = MassCertificationService.mapApiResponse(selectedCustomers);
    communicationStore.from = {name: route.name};

    router.push({
        name: 'patient-administration.communication.message.sms',
    });
};

const headers = [
    {
        title: "Patient",
        key: "customer"
    },
    {
        title: "Achats",
        key: "top_sales"
    },
    {
        title: "Dernier message",
        key: "last_message",
    }
];
</script>

<template>
  <BaseCard>
    <BaseCardBody>
      <div class="flex items-center justify-between gap-4">
        <div>
          <h1>Aidez vos patients du jour à bien prendre leurs médicaments</h1>
          <p>
            Envoyez un SMS à vos patients pour qu'ils retrouvent les notices de leurs achats et leurs ordonnances dans
            leur application Apodis !
          </p>
        </div>
        <div v-if="selected.length">
          <BaseButton primary density="comfortable" @click="handleSend">
            Envoyer un SMS à {{ selected.length }} {{ pluralize("patient", selected.length) }}
          </BaseButton>
        </div>
      </div>
      <v-data-table
        v-model="selected"
        :headers="headers"
        :item-value="item => item.id"
        show-select
        select-strategy="page"
        :items="data?.data ?? []"
        :loading="isValidating"
      >
        <template #[`item.customer`]="{ item }">
          <ConsumerAnimationCell
            :name="item.information?.name"
            :phone="item.information?.phone"
            :birth-date="item.information?.birth_date"
            :editable="false"
          />
        </template>
        <template #[`item.top_sales`]="{ item }">
          <ConsumerAnimationTopSales
            :products="(item.top_sales ?? [])
              .filter(product => product.product_code && product.product_name)
              .map(product => ({
                code: product.product_code as string,
                name: product.product_name as string,
                date: product.date
              }))
            "
          />
        </template>
        <template #[`item.last_message`]="{ item }">
          <p v-if="item.last_conversation_message">
            {{ dateHelper.mediumFormat(item.last_conversation_message.created_at) }}
          </p>
          <p v-else>
            Aucun message envoyé
          </p>
        </template>
      </v-data-table>
    </BaseCardBody>
  </BaseCard>
</template>

<style scoped>

</style>
